import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, MapPin, Instagram, Send, ArrowRight, Menu, X, Phone } from 'lucide-react';
import VkIcon from '../icons/VkIcon';
import ContactModal from '../ContactModal/ContactModal';
import './Header.css';
import { useServices } from '../../hooks/useServices';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';

const socialLinks = [
  { key: 'instagram', href: 'https://www.instagram.com/moscow_garage_?igsh=Y2pvYW9kN29uZnUz', label: 'Instagram', Icon: Instagram },
  { key: 'vk', href: 'https://vk.ru/moscow.garage', label: 'ВКонтакте', Icon: VkIcon },
  { key: 'telegram', href: 'https://t.me/MoscowGarageDeteiling', label: 'Telegram', Icon: Send },
];

const Header = () => {
  const { data: services = [] } = useServices();
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [mobileActiveSubmenu, setMobileActiveSubmenu] = useState(null);
  const [isContactOpen, setIsContactOpen] = useState(false);
  const servicesRef = useRef(null);
  const aboutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
        setActiveSubmenu(null);
      }
      if (aboutRef.current && !aboutRef.current.contains(event.target)) {
        setIsAboutOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useBodyScrollLock(isMobileMenuOpen);

  const aboutItems = [
    { label: 'Классификация машин', path: '/car-classification' },
    { label: 'Контроль качества', path: '/quality-control' },
    { label: 'Наши работы', path: '/our-works' },
    { label: 'Контакты', path: '/contacts' },
  ];

  const handleServiceMouseEnter = (index) => {
    setActiveSubmenu(index);
  };

  const handleServiceMouseLeave = () => {
    setActiveSubmenu(null);
  };

  const toggleMobileSubmenu = (index) => {
    setMobileActiveSubmenu(mobileActiveSubmenu === index ? null : index);
  };

  return (
    <>
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <Link to="/" className="logo">
            <span className="logo-white">МОСКОУ</span>
            <span className="logo-red">ГАРАЖ</span>
          </Link>

          <nav className="nav-container">
            <div className="nav-left">
              <div className="socials desktop-socials">
                {socialLinks.map(({ key, href, label, Icon }) => (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    aria-label={label}
                  >
                    <Icon size={20} />
                  </a>
                ))}
              </div>

              <Link to="/contacts" className="city">
                <MapPin size={16} />
                <span>Москва</span>
              </Link>

              <div className="nav-menu">
                <div className="dropdown" ref={servicesRef}>
                  <button
                    className={`dropdown-toggle ${isServicesOpen ? 'active' : ''}`}
                    onClick={() => setIsServicesOpen(!isServicesOpen)}
                  >
                    <span>Услуги</span>
                    <ChevronDown size={16} className="dropdown-icon" />
                  </button>
                  <div className={`dropdown-menu ${isServicesOpen ? 'open' : ''}`}>
                    {services.map((service, index) => (
                      <div
                        key={service.slug}
                        className="dropdown-item-wrapper"
                        onMouseEnter={() => handleServiceMouseEnter(index)}
                        onMouseLeave={handleServiceMouseLeave}
                      >
                        <Link
                          to={`/services/${service.slug}`}
                          className="dropdown-item has-submenu"
                          onClick={() => setIsServicesOpen(false)}
                        >
                          <span>{service.title}</span>
                          <ChevronRight size={14} className="submenu-arrow" />
                        </Link>
                        <div className={`dropdown-submenu ${activeSubmenu === index ? 'open' : ''}`}>
                          {service.subServices.map((subItem, subIndex) => (
                            <Link
                              key={subItem.slug || subIndex}
                              to={`/services/${service.slug}/${subItem.slug}`}
                              className="dropdown-subitem"
                              onClick={() => setIsServicesOpen(false)}
                            >
                              {subItem.title}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="dropdown" ref={aboutRef}>
                  <button
                    className={`dropdown-toggle ${isAboutOpen ? 'active' : ''}`}
                    onClick={() => setIsAboutOpen(!isAboutOpen)}
                  >
                    <span>О компании</span>
                    <ChevronDown size={16} className="dropdown-icon" />
                  </button>
                  <div className={`dropdown-menu ${isAboutOpen ? 'open' : ''}`}>
                    {aboutItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="dropdown-item"
                        onClick={() => setIsAboutOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
              
            </div>

            <div className="nav-right">
              <a href="tel:+79100002330" className="phone">
                <Phone size={16} />
                <span>+7 (910) 000-23-30</span>
              </a>

              <button type="button" className="contact-btn" onClick={() => setIsContactOpen(true)}>
                <span className="contact-btn-text">Связаться</span>
                <ArrowRight size={16} className="contact-btn-icon" />
              </button>
            </div>
          </nav>

          <div className="mobile-nav">
            <div className="socials mobile-socials">
              {socialLinks.map(({ key, href, label, Icon }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  aria-label={label}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>

            <button
              className="burger"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="Открыть меню"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div 
        className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`} 
        onClick={() => setIsMobileMenuOpen(false)} 
      />
      
      <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <Link
            to="/"
            className="logo"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <span className="logo-white">МОСКОУ</span>
            <span className="logo-red">ГАРАЖ</span>
          </Link>
          <button
            className="mobile-menu-close"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Закрыть меню"
          >
            <X size={24} />
          </button>
        </div>

        <div className="mobile-menu-section">
          <button
            className="mobile-dropdown-toggle"
            onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
          >
            <span>Услуги</span>
            <ChevronDown 
              size={20} 
              className={`mobile-dropdown-arrow ${mobileServicesOpen ? 'rotated' : ''}`}
            />
          </button>
          <div className={`mobile-dropdown-content ${mobileServicesOpen ? 'open' : ''}`}>
            {services.map((service, index) => (
              <div key={service.slug} className="mobile-service-group">
                <div className="mobile-service-toggle">
                  <Link
                    to={`/services/${service.slug}`}
                    className="mobile-service-link"
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      setMobileServicesOpen(false);
                      setMobileActiveSubmenu(null);
                    }}
                  >
                    {service.title}
                  </Link>
                  <button
                    className="mobile-service-arrow-btn"
                    onClick={() => toggleMobileSubmenu(index)}
                  >
                  <ChevronDown 
                    size={16} 
                    className={`mobile-submenu-arrow ${mobileActiveSubmenu === index ? 'rotated' : ''}`}
                  />
                  </button>
                </div>
                <div className={`mobile-submenu ${mobileActiveSubmenu === index ? 'open' : ''}`}>
                  {service.subServices.map((subItem, subIndex) => (
                    <Link
                      key={subItem.slug || subIndex}
                      to={`/services/${service.slug}/${subItem.slug}`}
                      className="mobile-submenu-item"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        setMobileServicesOpen(false);
                        setMobileActiveSubmenu(null);
                      }}
                    >
                      {subItem.title}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mobile-menu-section">
          <button
            className="mobile-dropdown-toggle"
            onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
          >
            <span>О компании</span>
            <ChevronDown 
              size={20} 
              className={`mobile-dropdown-arrow ${mobileAboutOpen ? 'rotated' : ''}`}
            />
          </button>
          <div className={`mobile-dropdown-content ${mobileAboutOpen ? 'open' : ''}`}>
            {aboutItems.map((item) => (
              <Link
                key={`mobile-${item.path}`}
                to={item.path}
                className="mobile-dropdown-item"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  setMobileAboutOpen(false);
                }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        <div className="mobile-info">
          <div className="mobile-city">
            <MapPin size={16} />
            <span>Москва</span>
          </div>
          <a href="tel:+79100002330" className="mobile-phone">
            <Phone size={18} />
            <span>+7 (910) 000-23-30</span>
          </a>
          <button
            type="button"
            className="mobile-contact-btn"
            onClick={() => {
              setIsMobileMenuOpen(false);
              setIsContactOpen(true);
            }}
          >
            <span>Связаться с нами</span>
            <ArrowRight size={18} />
          </button>

          <div className="mobile-socials-bottom">
            {socialLinks.map(({ key, href, label, Icon }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
                aria-label={label}
              >
                <Icon size={20} />
              </a>
            ))}
          </div>
        </div>
      </div>

      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} />
    </>
  );
};

export default Header;
